// FormFoldersPanel — the Forms-page folders sidebar: All forms / Unassigned
// forms, then the folder list, with "Manage folders" pinned to the bottom.
// Presentational: the page owns the fetch and the selection. Selecting a row
// only moves the active pill this chunk — the templates list is untouched,
// because POST /forms/all has no folder param yet (filtering client-side from
// `formIds` would be wrong against server-side pagination: a folder of 5 forms
// would show only the members that happen to sit on the current page).

import { useTranslation } from 'react-i18next';
import { Button, Icon } from '@/components/common';
import type { FormFolder } from '@/api/endpoints/formFoldersApi';

export type FolderSelection =
  | { kind: 'all' }
  | { kind: 'unassigned' }
  | { kind: 'folder'; id: string };

interface FormFoldersPanelProps {
  folders: FormFolder[];
  loading: boolean;
  error: boolean;
  /** Total forms in the active tab — the list query's own total. */
  allCount: number;
  selected: FolderSelection;
  onSelect: (selection: FolderSelection) => void;
  /** Opens the Manage-folders modal (create/rename/delete). Verb-gated by the
   *  page: absent = the Manage button doesn't render. */
  onManage?: () => void;
}

const FormFoldersPanel = ({
  folders,
  loading,
  error,
  allCount,
  selected,
  onSelect,
  onManage,
}: FormFoldersPanelProps) => {
  const { t } = useTranslation();

  // Forms filed in at least one folder — the rest of the tab's total is
  // unassigned. A form can only sit in one folder, but dedupe anyway so a
  // double-linked form can never push the remainder negative.
  const assigned = new Set(folders.flatMap((f) => f.formIds)).size;
  const unassignedCount = Math.max(0, allCount - assigned);

  const row = (
    key: string,
    icon: string,
    label: string,
    count: number,
    selection: FolderSelection,
    active: boolean,
  ) => (
    <button
      key={key}
      type='button'
      className={`forms-folders__row${active ? ' forms-folders__row--active' : ''}`}
      aria-pressed={active}
      onClick={() => onSelect(selection)}
    >
      <Icon name={icon} size={16} aria-hidden='true' />
      <span className='forms-folders__row-label'>{label}</span>
      <span className='forms-folders__row-count' aria-label={t('forms_folders_count', { count })}>
        {count}
      </span>
    </button>
  );

  return (
    <aside className='forms-folders' aria-label={t('forms_folders_section')}>
      {row(
        'all',
        'layers',
        t('forms_folders_all'),
        allCount,
        { kind: 'all' },
        selected.kind === 'all',
      )}
      {row(
        'unassigned',
        'folder',
        t('forms_folders_unassigned'),
        unassignedCount,
        { kind: 'unassigned' },
        selected.kind === 'unassigned',
      )}

      <p className='forms-folders__group-label'>{t('forms_folders_section')}</p>

      {loading ? (
        <p className='forms-folders__status'>{t('text_loading')}</p>
      ) : error ? (
        <p className='forms-folders__status'>{t('error_generic')}</p>
      ) : folders.length === 0 ? (
        <p className='forms-folders__status'>{t('forms_folders_empty')}</p>
      ) : (
        folders.map((folder) =>
          row(
            folder.id,
            'folder',
            folder.title,
            folder.formIds.length,
            { kind: 'folder', id: folder.id },
            selected.kind === 'folder' && selected.id === folder.id,
          ),
        )
      )}

      {onManage && (
        <Button
          className='forms-folders__manage'
          variant='secondary'
          iconStart='folder-plus'
          onClick={onManage}
        >
          {t('forms_folders_manage')}
        </Button>
      )}
    </aside>
  );
};

export default FormFoldersPanel;
